import { BrowserModule } from '@angular/platform-browser';
import { ErrorHandler, NgModule, Injector, APP_INITIALIZER, Inject } from '@angular/core';
import { IonicApp, IonicErrorHandler, IonicModule, NavController } from 'ionic-angular';
import { StatusBar } from '@ionic-native/status-bar';
import { SplashScreen } from '@ionic-native/splash-screen';
import { Keyboard } from '@ionic-native/keyboard';
import { MyApp } from './app.component';

// Import storage module
import { IonicStorageModule, Storage } from '@ionic/storage';
import { SecureStorage } from '@ionic-native/secure-storage';

// Import Http modules
import { HttpModule, Http } from '@angular/http';

// Import Barcode Scanner
import { BarcodeScanner } from '@ionic-native/barcode-scanner';

// Import Translate modules
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';


import { InAppBrowser } from '@ionic-native/in-app-browser';

import { AppVersion } from '@ionic-native/app-version';

import { Network } from '@ionic-native/network';

// Import pages
import { HomePage } from '../pages/home/home';
import { BasketPage } from '../pages/basket/basket';
import { AccountPage } from '../pages/account/account';
import { ContactPage } from '../pages/contact/contact';
import { LoginPage } from '../pages/login/login';
import { CategoryPage } from '../pages/category/category';
import { SortingPopoverPage } from '../pages/category/sorting-popover';
import { DetailPage } from '../pages/detail/detail';
import { RegisterPage } from '../pages/register/register';
import { ForgotpswdPage } from '../pages/forgotpswd/forgotpswd';
import { SearchPage } from '../pages/search/search';
import { OrderPage } from '../pages/order/order';
import { AddressPage } from '../pages/address/address';

// Import providers
import { AlertProvider } from '../providers/alert/alert';
import { LoadingProvider } from '../providers/loading/loading';
import { LoginProvider } from '../providers/login/login';
import { NavigationListProvider } from '../providers/navigation-list/navigation-list';
import { BasketProvider } from '../providers/basket/basket';
import { ScanProvider } from '../providers/scan/scan';
import { AccountProvider } from '../providers/account/account';
import { CompanyProvider } from '../providers/company/company';
import { CategoryProvider } from '../providers/category/category';
import { ProductProvider } from '../providers/product/product';
import { AuthProvider } from '../providers/auth/auth';
import { AddressProvider } from '../providers/address/address';

import { PipesModule } from '../pipes/pipes.module';
import { AppConfig } from '../config/app.config';

// Import mocks
import { ProductProviderMock } from '../providers/product/product-mock';
import { CategoryProviderMock } from '../providers/category/category-mock';
import { CompanyProviderMock } from '../providers/company/company-mock';
import { AccountProviderMock } from '../providers/account/account-mock';
import { LoginProviderMock } from '../providers/login/login-mock';
import { AddressProviderMock } from '../providers/address/address-mock';
import { FavoriteProvider } from '../providers/favorite/favorite';
import { OrderProvider } from '../providers/order/order';
import { MenuPage } from '../pages/menu/menu';
import { CompanyConfigProvider } from '../providers/company-config/company-config';
import { MyOrdersPage } from '../pages/my-orders/my-orders';
import { MyOrderPage } from '../pages/my-order/my-order';
import { BannerProviderMock } from '../providers/banner/banner-mock';
import { BannerProvider } from '../providers/banner/banner';
import { NetworkInfoProvider } from '../providers/network-info/network-info';

// location of app config
const APP_CONFIG = './assets/config/device.toffer.json';
const MOCK = false;

export function createTranslateLoader(http: Http) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

export function productProviderFactory() {
  if (MOCK) {
    return new ProductProviderMock();
  } else {
    return new ProductProvider();
  }
}

export function categoryProviderFactory(){
  if (MOCK) {
    return new CategoryProviderMock();
  } else {
    return new CategoryProvider();
  }
}

export function companyProviderFactory(){
  if (MOCK) {
    return new CompanyProviderMock();
  } else {
    return new CompanyProvider();
  }
}

export function accountProviderFactory(){
  if (MOCK) {
    return new AccountProviderMock();
  } else {
    return new AccountProvider();
  }
}

export function loginProviderFactory(http: Http, storage: Storage, appConfig: AppConfig) {
  if (MOCK) {
    return new LoginProviderMock();
  } else {
    return new LoginProvider(http, storage, appConfig);
  }
}

export function addressProviderFactory(loginProvider: LoginProvider){
  if (MOCK) {
    return new AddressProviderMock();
  } else {
    return new AddressProvider(loginProvider);
  }
}

export function bannerProviderFactory(){
  if (MOCK) {
    return new BannerProviderMock();
  } else {
    return new BannerProvider();
  }
}

export function appConfigFactory(http: Http){
  return new AppConfig(http, APP_CONFIG);
}

@NgModule({
  declarations: [
    MyApp,
    HomePage,
    BasketPage,
    AccountPage,
    ContactPage,
    LoginPage,
    CategoryPage,
    DetailPage,
    RegisterPage,
    ForgotpswdPage,
    SearchPage,
    OrderPage,
    AddressPage,
    SortingPopoverPage,
    MenuPage,
    MyOrdersPage,
    MyOrderPage
  ],
  imports: [
    BrowserModule,
    IonicModule.forRoot(MyApp),
    IonicStorageModule.forRoot(),
    HttpModule,
    PipesModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: (createTranslateLoader),
        deps: [Http]
      }
    })
  ],
  bootstrap: [IonicApp],
  entryComponents: [
    MyApp,
    HomePage,
    BasketPage,
    AccountPage,
    ContactPage,
    LoginPage,
    CategoryPage,
    DetailPage,
    RegisterPage,
    ForgotpswdPage,
    SearchPage,
    OrderPage,
    AddressPage,
    SortingPopoverPage,
    MenuPage,
    MyOrdersPage,
    MyOrderPage
  ],
  providers: [
    AuthProvider,
    {
      provide: ProductProvider,
      useFactory: productProviderFactory
    },
    {
      provide: CategoryProvider,
      useFactory: categoryProviderFactory
    },
    {
      provide: BannerProvider,
      useFactory: bannerProviderFactory
    },
    {
      provide: CompanyProvider,
      useFactory: categoryProviderFactory
    },
    {
      provide: AccountProvider,
      useFactory: accountProviderFactory
    },
    {
      provide: LoginProvider,
      useFactory: loginProviderFactory,
      deps: [Http, Storage, AppConfig]
    },
    {
      provide: AddressProvider,
      useFactory: addressProviderFactory,
      deps: [LoginProvider]
    },
    {
      provide: AppConfig,
      useFactory: appConfigFactory,
      deps: [Http]
    },
    StatusBar,
    SplashScreen,
    { provide: ErrorHandler, useClass: IonicErrorHandler },
    AlertProvider,
    NavigationListProvider,
    BasketProvider,
    LoadingProvider,
    ScanProvider,
    BarcodeScanner,
    Keyboard,
    FavoriteProvider,
    OrderProvider,
    CompanyConfigProvider,
    InAppBrowser,
    AppVersion,
    Network,
    NetworkInfoProvider,
    NetworkInfoProvider
  ]
})
export class AppModule {
  /**
    * Allows for retrieving singletons using `AppModule.injector.get(MyService)`
    * This is good to prevent injecting the service as constructor parameter.
    */
  static injector: Injector;
  constructor(injector: Injector) {
    AppModule.injector = injector;
  }
}



// WEBPACK FOOTER //
// ./src/app/app.module.ts